import React, { useEffect, useMemo, useState } from "react";

import { ChevronDown, ChevronUp } from "lucide-react";

import { normalizeUserId } from "./disconnectResolutionUtils";
import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import {
  avatarColor,
  initials,
} from "../../Container/AssessmentsContainer/DisconnectTriggerModal";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchOrganizationLevels } from "../../Store/Reducers/AdminSlice";
import {
  DisconnectResolution,
  DisconnectSponsorAssessment,
} from "../../Store/Reducers/AssessmentSlice";
import {
  getDisconnectActionLabel,
  getDisconnectActionPillBadgeClass,
} from "../../Utils/Constants/disconnectStatusLabels";
import {
  isUtcDateOverdue,
  parseAssessmentDate,
} from "../../Utils/Helper/assessmentDateUtils";
import { formatDateTime } from "../../Utils/Helper/Helper";
import {
  extractLevelName,
  getAccountLevelCompactLabel,
} from "../../Utils/Helper/levelUtils";

type OrganizationLevel = {
  level_code: string;
  name: string;
};

const LEVEL_SCALE_COLORS = [
  "#94a3b8",
  "#f87171",
  "#fb923c",
  "#fbbf24",
  "#60a5fa",
  "#4ade80",
  "#a78bfa",
  "#34d399",
];

const EMPTY_SPONSORS: DisconnectSponsorAssessment[] = [];

/** Written when ESL Click-to-Override publishes and closes an open disconnect. */
const ESL_OVERRIDE_CLOSED_DISCONNECT_RATIONALE =
  "ESL override published this account and closed the disconnect.";

function isEslOverrideClosedJustification(
  justification: string | null | undefined
): boolean {
  return (
    (justification?.trim() || "") === ESL_OVERRIDE_CLOSED_DISCONNECT_RATIONALE
  );
}

function findOrgLevel(
  levelNum: number,
  organizationLevels: OrganizationLevel[]
): OrganizationLevel | undefined {
  return organizationLevels.find(item => {
    const codeNum = Number(String(item.level_code).replace(/^L/i, ""));
    return Number.isFinite(codeNum) && codeNum === levelNum;
  });
}

function apiLevelDisplay(
  levelNum: number | null,
  levelLabel: string | null | undefined,
  organizationLevels: OrganizationLevel[]
): string {
  if (levelNum == null) {
    return levelLabel?.trim() || "Not submitted";
  }
  const orgLevel = findOrgLevel(levelNum, organizationLevels);
  return getAccountLevelCompactLabel(
    levelNum,
    orgLevel?.name ?? extractLevelName(levelLabel)
  );
}

function levelPosition(level: number, maxLevel = 7) {
  return `${(level / maxLevel) * 100}%`;
}

function medianOfLevels(levels: number[]): number {
  const sorted = [...levels].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? sorted[mid - 1] : sorted[mid];
}

/** Prefer sponsor snapshots when stored min/max were mis-saved as all zeros. */
function resolveDistributionLevels(
  resolution: DisconnectResolution,
  sponsors: DisconnectSponsorAssessment[]
): {
  minLevel: number;
  maxLevel: number;
  medianLevel: number;
  eslFinalLevel: number;
} {
  const sponsorLevels = sponsors
    .map(s => s.level)
    .filter((n): n is number => typeof n === "number" && Number.isFinite(n));

  const storedMin = resolution.min_level ?? 0;
  const storedMax = resolution.max_level ?? 0;
  const storedMedian = resolution.median_level ?? 0;
  const storedEsl = resolution.esl_final_level ?? 0;

  let minLevel = storedMin;
  let maxLevel = storedMax;
  let medianLevel = storedMedian;

  if (sponsorLevels.length >= 2) {
    const derivedMin = Math.min(...sponsorLevels);
    const derivedMax = Math.max(...sponsorLevels);
    const derivedMedian = medianOfLevels(sponsorLevels);
    const storedSpread = storedMax - storedMin;
    const derivedSpread = derivedMax - derivedMin;
    const storedLooksFlatWrong = storedSpread === 0 && derivedSpread > 0;
    const storedMismatchesSponsors = storedSpread !== derivedSpread;

    if (storedLooksFlatWrong || storedMismatchesSponsors) {
      minLevel = derivedMin;
      maxLevel = derivedMax;
      medianLevel = derivedMedian;
    }
  }

  const eslFinalLevel =
    typeof resolution.agreed_level === "number" &&
    Number.isFinite(resolution.agreed_level)
      ? resolution.agreed_level
      : sponsorLevels.length >= 2 &&
          storedEsl === storedMin &&
          storedEsl === storedMax &&
          minLevel !== maxLevel
        ? medianLevel
        : (resolution.esl_final_level ?? medianLevel);

  return { minLevel, maxLevel, medianLevel, eslFinalLevel };
}

function getDaysOverdue(dueDate?: string | null): number {
  if (!dueDate) return 0;
  const due = parseAssessmentDate(dueDate);
  if (!due || !isUtcDateOverdue(dueDate)) return 0;
  const today = new Date();
  const todayMs = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate()
  );
  const dueMs = Date.UTC(
    due.getUTCFullYear(),
    due.getUTCMonth(),
    due.getUTCDate()
  );
  return Math.floor((todayMs - dueMs) / 86400000);
}

function formatShortDate(value?: string | null): string {
  if (!value) return "—";
  const formatted = formatDateTime(value, "DMY_SHORT").date;
  return formatted && formatted !== "-" ? formatted : "—";
}

function LevelSummaryCard({
  label,
  levelNum,
  organizationLevels,
  theme,
}: {
  label: string;
  levelNum: number;
  organizationLevels: OrganizationLevel[];
  theme: "orange" | "blue" | "purple" | "teal";
}) {
  const themes = {
    orange: "border-orange-200 bg-orange-50 text-orange-800",
    blue: "border-sky-200 bg-sky-50 text-sky-800",
    purple: "border-purple-200 bg-purple-50 text-purple-800",
    teal: "border-teal-200 bg-teal-50 text-teal-800",
  };

  return (
    <div className={`rounded-lg border p-3 ${themes[theme]}`}>
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wider opacity-80">
        {label}
      </div>
      <div className="text-sm font-semibold">
        {apiLevelDisplay(levelNum, `Level ${levelNum}`, organizationLevels)}
      </div>
    </div>
  );
}

function LevelDistributionBar({
  minLevel,
  medianLevel,
  maxLevel,
  eslFinalLevel,
  organizationLevels,
}: {
  minLevel: number;
  medianLevel: number;
  maxLevel: number;
  eslFinalLevel: number;
  organizationLevels: OrganizationLevel[];
}) {
  const maxScale = 7;
  const baseMarkers = [
    {
      key: "min",
      level: minLevel,
      cls: "bg-orange-500",
      label: `Min: ${apiLevelDisplay(minLevel, `Level ${minLevel}`, organizationLevels)}`,
      textCls: "text-orange-600",
    },
    {
      key: "median",
      level: medianLevel,
      cls: "bg-sky-500",
      label: `Median: ${apiLevelDisplay(medianLevel, `Level ${medianLevel}`, organizationLevels)}`,
      textCls: "text-sky-600",
    },
    {
      key: "max",
      level: maxLevel,
      cls: "bg-purple-600",
      label: `Max: ${apiLevelDisplay(maxLevel, `Level ${maxLevel}`, organizationLevels)}`,
      textCls: "text-purple-600",
    },
    {
      key: "final",
      level: eslFinalLevel,
      cls: "bg-teal-600 ring-2 ring-white",
      label: `Final: ${apiLevelDisplay(eslFinalLevel, `Level ${eslFinalLevel}`, organizationLevels)}`,
      textCls: "text-teal-600",
      larger: true,
    },
  ];

  const markers = baseMarkers.map((marker, index) => {
    const overlapIndex = baseMarkers
      .slice(0, index)
      .filter(other => other.level === marker.level).length;
    return { ...marker, overlapIndex };
  });

  return (
    <div className="rounded-lg border border-gray-200 bg-slate-50 p-3.5">
      <div className="mb-3 text-[10.5px] font-bold uppercase tracking-wider text-gray-500">
        Sponsor Level Distribution &amp; Final Level
      </div>
      <div className="relative mb-3.5">
        <div className="flex h-2.5 overflow-hidden rounded-md">
          {LEVEL_SCALE_COLORS.map((color, index) => (
            <div
              key={index}
              className="flex-1"
              style={{
                backgroundColor: `${color}33`,
                borderRight:
                  index < LEVEL_SCALE_COLORS.length - 1
                    ? `1px solid ${color}66`
                    : undefined,
              }}
            />
          ))}
        </div>
        {markers.map(marker => (
          <div
            key={marker.key}
            className="absolute top-1/2 z-10"
            style={{ left: levelPosition(marker.level, maxScale) }}
          >
            <div
              className={`rounded-full border-2 border-white shadow ${marker.cls} ${
                marker.larger ? "h-[18px] w-[18px]" : "h-4 w-4"
              }`}
              style={{
                transform: `translate(calc(-50% + ${marker.overlapIndex * 7}px), -50%)`,
              }}
            />
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {markers.map(marker => (
          <div
            key={marker.key}
            className={`flex items-center gap-1 text-[11px] font-semibold ${marker.textCls}`}
          >
            <span
              className={`h-2 w-2 rounded-full ${marker.cls.split(" ")[0]}`}
            />
            {marker.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function SponsorAssessmentRow({
  sponsor,
  organizationLevels,
  isPrimary,
}: {
  sponsor: DisconnectSponsorAssessment;
  organizationLevels: OrganizationLevel[];
  isPrimary: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const levelNum =
    typeof sponsor.level === "number" && Number.isFinite(sponsor.level)
      ? sponsor.level
      : null;
  const hasRationale = Boolean(sponsor.rationale?.trim());

  return (
    <div className="overflow-hidden rounded-md border border-gray-200">
      <button
        type="button"
        className="flex w-full items-center gap-3 bg-white px-3.5 py-3 text-left hover:bg-gray-50"
        onClick={() => {
          if (!hasRationale) return;
          setExpanded(prev => !prev);
        }}
        aria-expanded={hasRationale ? expanded : undefined}
      >
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
          style={{ backgroundColor: avatarColor(sponsor.name) }}
        >
          {initials(sponsor.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-semibold text-gray-900">
              {sponsor.name}
            </span>
            {isPrimary && (
              <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700 ring-1 ring-violet-200 bg-violet-50">
                Primary
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500">
            {sponsor.title || sponsor.role || "Executive Sponsor"}
          </div>
        </div>
        <span className="inline-flex shrink-0 items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
          {apiLevelDisplay(levelNum, sponsor.levelLabel, organizationLevels)}
        </span>
        {hasRationale ? (
          expanded ? (
            <ChevronUp className="h-4 w-4 shrink-0 text-gray-400" size={16} />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" size={16} />
          )
        ) : (
          <span className="h-4 w-4 shrink-0" aria-hidden />
        )}
      </button>
      {expanded && hasRationale && (
        <div className="border-t border-gray-100 bg-gray-50 px-3.5 py-3 text-sm leading-relaxed text-gray-600">
          {sponsor.rationale}
        </div>
      )}
    </div>
  );
}

export interface DisconnectResolutionDetailModalProps {
  isOpen: boolean;
  resolution: DisconnectResolution | null;
  onClose: () => void;
  onRecordResolution?: (resolution: DisconnectResolution) => void;
  canRecordResolution?: boolean;
  canEditResolution?: boolean;
}

const DisconnectResolutionDetailModal: React.FC<
  DisconnectResolutionDetailModalProps
> = ({
  isOpen,
  resolution,
  onClose,
  onRecordResolution,
  canRecordResolution = false,
  canEditResolution = false,
}) => {
  const dispatch = useAppDispatch();
  const organizationLevels = useAppSelector(
    state => state.admin.organizationLevels
  ) as OrganizationLevel[];

  useEffect(() => {
    if (isOpen && organizationLevels.length === 0) {
      dispatch(fetchOrganizationLevels());
    }
  }, [dispatch, isOpen, organizationLevels.length]);

  const sponsors = resolution?.sponsor_assessments ?? EMPTY_SPONSORS;
  const primarySponsorId = normalizeUserId(resolution?.primarySponsorId);

  const primarySponsor = useMemo(
    () =>
      sponsors.find(s => normalizeUserId(s.executiveId) === primarySponsorId) ??
      null,
    [sponsors, primarySponsorId]
  );

  const distribution = useMemo(() => {
    if (!resolution) {
      return { minLevel: 0, maxLevel: 0, medianLevel: 0, eslFinalLevel: 0 };
    }
    return resolveDistributionLevels(resolution, sponsors);
  }, [resolution, sponsors]);

  if (!resolution) return null;

  const { minLevel, maxLevel, medianLevel, eslFinalLevel } = distribution;
  const isPending = resolution.status === "pending_resolution";
  const isResolved = resolution.status === "resolved";
  const isPublished =
    resolution.status === "published" || resolution.hubPublished === true;
  const justification = resolution.resolution_justification?.trim() || "";
  const closedByEslOverride =
    isEslOverrideClosedJustification(justification) &&
    !resolution.resolved_at &&
    !resolution.resolvedByName?.trim();
  // Real PS record — do not treat ESL-override agreed_level alone as PS submit.
  const hasPsSubmission =
    Boolean(resolution.resolved_at) ||
    Boolean(resolution.resolvedByName?.trim());
  const isManualTrigger = Boolean(resolution.context_note?.trim());
  const isStrong = resolution.delta_tier === "strong";
  const overdueDays = getDaysOverdue(resolution.due_date);
  const showOverdue = isPending && overdueDays > 0;
  const showRecordButton =
    canRecordResolution && isPending && Boolean(onRecordResolution);
  const showEditButton =
    canEditResolution && isResolved && Boolean(onRecordResolution);

  const triggeredLabel = formatShortDate(resolution.triggered_at);
  const dueLabel = formatShortDate(resolution.due_date);
  const resolvedLabel = formatShortDate(resolution.resolved_at);
  const publishedLabel = formatShortDate(resolution.published_at);

  const psLevelNum =
    typeof resolution.agreed_level === "number" &&
    Number.isFinite(resolution.agreed_level)
      ? resolution.agreed_level
      : eslFinalLevel;
  const psSubmittedBy =
    resolution.resolvedByName?.trim() ||
    primarySponsor?.name?.trim() ||
    "Primary sponsor";
  const overrideClosedBy =
    resolution.publishedByName?.trim() ||
    resolution.triggeredByName?.trim() ||
    "ESL";
  const publicationSubtitle = closedByEslOverride
    ? "Closed by ESL override"
    : resolution.publishedByName?.trim()
      ? "Accepted and published by ESL"
      : "Auto-published by system";

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      size="4xl"
      title={`Disconnect Resolution: ${resolution.accountName || "Account"}`}
      contentClassName="overflow-y-auto max-h-[70vh]"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-sm text-gray-500">
            Triggered by {resolution.triggeredByName || "System"} ·{" "}
            {triggeredLabel}
          </span>
          <div className="flex items-center gap-2">
            {showRecordButton && (
              <Button
                variant="primary"
                onClick={() => onRecordResolution?.(resolution)}
              >
                Record Resolution
              </Button>
            )}
            {showEditButton && (
              <Button
                variant="primary"
                onClick={() => onRecordResolution?.(resolution)}
              >
                Edit Resolution
              </Button>
            )}
            <Button variant="outline-primary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Disconnect details */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Disconnect Details
            </div>
            <div className="mt-1 text-xs text-gray-500">
              Triggered {triggeredLabel} · Due {dueLabel} ·{" "}
              {isManualTrigger ? "Manually triggered" : "Auto triggered"}
            </div>
            {isPending && (
              <p className="mb-0 mt-2 text-xs leading-relaxed text-gray-600">
                {canRecordResolution
                  ? "Record your resolution to continue. The result stays pending until the disconnect due date."
                  : "Publishing stays pending until the primary sponsor records a resolution or the disconnect due date."}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                isStrong
                  ? "border-red-200 bg-red-50 text-red-700"
                  : "border-amber-200 bg-amber-50 text-amber-700"
              }`}
            >
              {isStrong ? "Strong" : "Soft"} disconnect · Δ
              {resolution.delta_value}
            </span>
            <span
              className={getDisconnectActionPillBadgeClass(
                resolution.status,
                resolution.hubPublished
              )}
            >
              {getDisconnectActionLabel(
                resolution.status,
                resolution.hubPublished
              )}
            </span>
            {showOverdue && (
              <span className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-[11px] font-semibold text-orange-700">
                Overdue by {overdueDays} day{overdueDays !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>

        {/* Level summary cards */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <LevelSummaryCard
            label="Min Assessed"
            levelNum={minLevel}
            organizationLevels={organizationLevels}
            theme="orange"
          />
          <LevelSummaryCard
            label="Median"
            levelNum={medianLevel}
            organizationLevels={organizationLevels}
            theme="blue"
          />
          <LevelSummaryCard
            label="Max Assessed"
            levelNum={maxLevel}
            organizationLevels={organizationLevels}
            theme="purple"
          />
          <LevelSummaryCard
            label="Final Level"
            levelNum={eslFinalLevel}
            organizationLevels={organizationLevels}
            theme="teal"
          />
        </div>

        <LevelDistributionBar
          minLevel={minLevel}
          medianLevel={medianLevel}
          maxLevel={maxLevel}
          eslFinalLevel={eslFinalLevel}
          organizationLevels={organizationLevels}
        />

        {/* Primary sponsor resolution (real PS submit only) */}
        {hasPsSubmission && (
          <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3.5">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-violet-700">
                  Primary Sponsor Resolution
                </div>
                <div className="mt-1 text-sm font-semibold text-violet-950">
                  {apiLevelDisplay(
                    psLevelNum,
                    `Level ${psLevelNum}`,
                    organizationLevels
                  )}
                </div>
              </div>
              <span className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-violet-700 ring-1 ring-violet-200">
                PS submitted
              </span>
            </div>
            <div className="mt-1.5 text-xs text-violet-800/80">
              Submitted by {psSubmittedBy} · {resolvedLabel}
            </div>
            {justification &&
              !isEslOverrideClosedJustification(justification) && (
                <div className="mt-3 rounded-md border border-violet-100 bg-white p-3">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                    Justification
                  </div>
                  <p className="m-0 text-sm leading-relaxed text-gray-700">
                    {justification}
                  </p>
                </div>
              )}
          </div>
        )}

        {/* ESL override closed disconnect (PS never resolved) */}
        {closedByEslOverride && (
          <div className="rounded-lg border border-purple-200 bg-purple-50/60 p-3.5">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-purple-700">
                  ESL override closed disconnect
                </div>
                <div className="mt-1 text-sm font-semibold text-purple-950">
                  {apiLevelDisplay(
                    psLevelNum,
                    `Level ${psLevelNum}`,
                    organizationLevels
                  )}
                </div>
              </div>
              <span className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-purple-700 ring-1 ring-purple-200">
                ESL override
              </span>
            </div>
            <div className="mt-1.5 text-xs text-purple-800/80">
              Closed by {overrideClosedBy}
              {publishedLabel !== "—" ? ` · ${publishedLabel}` : ""}
            </div>
            {justification && (
              <div className="mt-3 rounded-md border border-purple-100 bg-white p-3">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  Note
                </div>
                <p className="m-0 text-sm leading-relaxed text-gray-700">
                  {justification}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Publication */}
        {!isPending &&
          (hasPsSubmission ||
            closedByEslOverride ||
            isResolved ||
            isPublished) && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3.5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Publication
              </div>
              {isPublished ? (
                <>
                  <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-gray-900">
                      Published by{" "}
                      {resolution.publishedByName?.trim() || "System"}
                    </div>
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700 ring-1 ring-gray-200">
                      Published
                    </span>
                  </div>
                  <div className="mt-1.5 text-xs text-gray-500">
                    {publishedLabel} · {publicationSubtitle}
                  </div>
                </>
              ) : (
                <p className="mb-0 mt-1 text-sm text-gray-600">
                  Pending until disconnect due. Not published yet.
                </p>
              )}
            </div>
          )}

        {/* Old assessment responses */}
        <div>
          <div className="mb-3 text-[10.5px] font-bold uppercase tracking-wider text-gray-500">
            Old Assessment Responses ({sponsors.length})
          </div>
          <div className="flex flex-col gap-2">
            {sponsors.map((sponsor, index) => (
              <SponsorAssessmentRow
                key={`${sponsor.executiveId || sponsor.name}-${index}`}
                sponsor={sponsor}
                organizationLevels={organizationLevels}
                isPrimary={
                  Boolean(primarySponsorId) &&
                  normalizeUserId(sponsor.executiveId) === primarySponsorId
                }
              />
            ))}
          </div>
        </div>
      </div>
    </CommonModal>
  );
};

export default DisconnectResolutionDetailModal;
